import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { useAuth } from "@/auth/auth";
import { openPalette } from "./CommandPalette";
import { openJarvis } from "@/jarvis/jarvis-store";
import { NotificationsPopover } from "./NotificationsPopover";
import { ThemeToggle } from "@/design/ThemeToggle";

/**
 * Ikonový rail (artboard p1a) — náhrada horní lišty s devíti odkazy.
 *
 * Důvod změny je jediný: rozhodnutí byla schovaná mezi ostatními položkami,
 * takže inbox nikdo neviděl. V railu je nese vlastní ikona s počítadlem.
 * Adresy se nemění — rail mapuje 1:1 na stávající routy (poznámka p7c/4),
 * proto je jich devět, i když artboard kreslí jen šest.
 *
 * Ikona nese `aria-label` s tímtéž textem, jaký měl odkaz v liště: čtečka
 * i testy tak pořád najdou „PROJEKTY", ne piktogram.
 */

const NAV_ITEMS: { to: string; label: string; icon: string }[] = [
  { to: "/", label: cs.nav.command, icon: "◉" },
  { to: "/projects", label: cs.nav.projects, icon: "▤" },
  { to: "/plan", label: cs.nav.plan, icon: "◧" },
  // Čtyři samostatné položky, ne jedna schránka se záložkami: úkol ownera
  // zdrží o minuty, blokace stojí celý cyklus workera. Každá má vlastní
  // počítadlo, jinak by se naléhavost zase slila do jednoho čísla.
  { to: "/decisions", label: cs.nav.decisions, icon: "▲" },
  { to: "/human-tasks", label: cs.nav.humanTasks, icon: "✋" },
  { to: "/blockers", label: cs.nav.blockers, icon: "⛔" },
  { to: "/proposals", label: cs.nav.proposals, icon: "✧" },
  { to: "/goals", label: cs.nav.goals, icon: "✦" },
  { to: "/workers", label: cs.nav.workers, icon: "⚙" },
  { to: "/economy", label: cs.nav.economy, icon: "◍" },
  { to: "/brain", label: cs.nav.brain, icon: "❖" },
  { to: "/departments", label: cs.nav.departments, icon: "⊞" },
  { to: "/audit", label: cs.nav.audit, icon: "≡" },
];

const ITEM = "relative flex h-11 w-11 shrink-0 items-center justify-center text-[17px]";

export function NavRail() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // počty pro každou ze čtyř schránek — sdílí cache s obrazovkami samotnými
  const inbox = useQuery({
    queryKey: ["inbox"],
    queryFn: () => api.decisionInbox(),
    refetchInterval: 30_000,
  });
  const humanTasks = useQuery({
    queryKey: ["human-tasks"],
    queryFn: () => api.humanTasks(),
    refetchInterval: 30_000,
  });
  const blockers = useQuery({
    queryKey: ["blockers", false],
    queryFn: () => api.blockers({ limit: 100 }),
    refetchInterval: 60_000,
  });
  const proposals = useQuery({
    queryKey: ["proposals-inbox", false],
    queryFn: () => api.proposalInbox(),
    refetchInterval: 60_000,
  });
  const badges: Record<string, number> = {
    "/decisions": inbox.data?.items.length ?? 0,
    "/human-tasks": humanTasks.data?.items.length ?? 0,
    // počet ze serveru, ne z délky výpisu — ten je omezený a ukazoval by strop
    "/blockers": blockers.data?.groupTotal ?? 0,
    "/proposals": proposals.data?.items.length ?? 0,
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  return (
    <nav
      // z-index není kosmetika: `backdrop-filter` dělá z railu vlastní vrstvu,
      // a bez explicitního pořadí se přes něj (a přes jeho menu) kreslily
      // karty z obsahu — panely jsou `relative`, takže mají přednost
      // bez `overflow-hidden`: rail ořezával vlastní vyskakovací menu (účet,
      // notifikace) — z nabídky koukal jen proužek u okraje a do nastavení
      // se nedalo dostat. Přetečení řeší scrollovací seznam ikon níž.
      className="sticky top-0 z-40 flex h-screen w-[76px] shrink-0 flex-col items-center py-[22px]"
      style={{
        background: "var(--surface-overlay)",
        borderRight: "1px solid var(--border-subtle)",
        backdropFilter: "var(--surface-blur)",
      }}
      aria-label={cs.nav.rail}
    >
      <Link to="/" aria-label={cs.app.name} className="mb-[18px] shrink-0">
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: "var(--accent-gradient)",
            boxShadow: "var(--emphasis-accent)",
          }}
        />
      </Link>

      {/* ikony se scrollují samy — s desátou položkou přerostl rail výšku
          okna a spodní skupina (Jarvis, zvonek, účet) vypadla ven */}
      <div className="flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto">
      {NAV_ITEMS.map((item) => {
        const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
        const badge = badges[item.to] ?? 0;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
            title={item.label}
            className={ITEM}
            style={{
              borderRadius: 14,
              // aktivní ikona stojí na akcentní ploše — plný tón by si tam kontrast ujedl
              color: active ? "var(--accent-on-tint)" : "var(--text-tertiary)",
              background: active ? "var(--accent-bg)" : "transparent",
              boxShadow: active ? "inset 0 0 0 1px var(--accent-border)" : undefined,
            }}
          >
            <span aria-hidden>{item.icon}</span>
            {badge > 0 ? (
              <span
                className="absolute right-1 top-1 rounded-lg px-1 text-[11px] font-extrabold leading-[13px]"
                style={{
                  background: "var(--critical)",
                  color: "var(--text-on-accent)",
                  boxShadow: "var(--emphasis-critical)",
                }}
                data-testid={`rail-badge-${item.to.replace("/", "")}`}
              >
                {badge}
              </span>
            ) : null}
          </Link>
        );
      })}

      <button
        type="button"
        onClick={openPalette}
        className={`${ITEM} cursor-pointer text-[12px] tracking-[0.08em]`}
        style={{ borderRadius: 14, color: "var(--text-tertiary)" }}
        aria-label={cs.nav.palette}
        title={cs.nav.palette}
      >
        ⌘K
      </button>
      </div>

      <div className="flex shrink-0 flex-col items-center gap-3 pt-3">
        <button
          type="button"
          onClick={() => openJarvis()}
          className="af-animate-pulse cursor-pointer"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "radial-gradient(circle at 35% 35%, #8ff2ff, #0e7f96 60%, #1b3a55)",
            boxShadow: "var(--emphasis-accent)",
          }}
          aria-label={cs.jarvis.name}
          title={cs.jarvis.name}
          data-testid="jarvis-toggle"
        />

        <NotificationsPopover placement="rail" />

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center text-[12px] font-bold tracking-[0.06em]"
            style={{
              borderRadius: "50%",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-strong)",
            }}
            aria-label={session?.user.name ?? cs.nav.account}
            aria-expanded={menuOpen}
            title={session?.user.name}
            data-testid="user-menu"
          >
            {initials(session?.user.name)}
          </button>

          {menuOpen ? (
            <div
              className="af-animate-fade absolute bottom-0 left-[46px] z-50 w-56 overflow-hidden"
              style={{
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-card)",
                background: "var(--surface-overlay)",
                boxShadow: "var(--shadow-panel)",
                backdropFilter: "var(--surface-blur)",
              }}
            >
              <div className="px-4 pb-1 pt-3">
                <div className="af-label-dim mb-1.5">{cs.topbar.theme}</div>
                <ThemeToggle />
              </div>
              <div className="my-2" style={{ borderTop: "1px solid var(--border-subtle)" }} />
              {session && session.user.role !== "CLIENT" ? (
                <button
                  type="button"
                  className="block w-full cursor-pointer px-4 py-2.5 text-left text-[12px] tracking-[0.14em]"
                  style={{ color: "var(--text-secondary)" }}
                  onClick={() => {
                    setMenuOpen(false);
                    void navigate({ to: "/settings" });
                  }}
                >
                  {cs.nav.settings}
                </button>
              ) : null}
              <button
                type="button"
                className="block w-full cursor-pointer px-4 py-2.5 text-left text-[12px] tracking-[0.14em]"
                style={{ color: "var(--critical)" }}
                data-testid="logout"
                onClick={() => {
                  setMenuOpen(false);
                  void logout().then(() => navigate({ to: "/login" }));
                }}
              >
                {cs.auth.logout}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </nav>
  );
}

function initials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}
